import { useEffect, useState } from 'react';
import { TEvent, TJob, TSapMo } from '../../../lib/types';
import { GET_JOBS_BY_EVENT, GET_MO_NUMBERS_BY_REV_CODE } from '../../../graph-queries/tar/job';
import { useLazyQuery } from '@apollo/client';
import { useSelectedAsset } from '../../../lib/util';

export function getJobsDelta(
  companionMos: string[],
  sapMos: string[]
): { moNumber: string; inCompanion: boolean; inSap: boolean }[] {
  const uniqueValues = new Set([...companionMos, ...sapMos]);

  const companionSet = new Set(companionMos);
  const sapSet = new Set(sapMos);

  return Array.from(uniqueValues)
    .map((value) => ({
      moNumber: value,
      inCompanion: companionSet.has(value),
      inSap: sapSet.has(value),
    }))
    .filter((delta) => !(delta.inCompanion && delta.inSap));
}
/**
 * Computes the delta for each pair of sub-arrays in two 2D arrays.
 * @param companionMos2D - 2D array of strings representing companion MOS data.
 * @param sapMos2D - 2D array of strings representing SAP MOS data.
 * @returns Array of deltas for each sub-array pair, preserving the index of comparison.
 */
function getJobsDeltaForAll(
  companionMos2D: string[][],
  sapMos2D: string[][]
): { delta: { moNumber: string; inCompanion: boolean; inSap: boolean }[] }[] {
  // Compute the number of pairs to process
  const maxLength = Math.max(companionMos2D.length, sapMos2D.length);

  // Map over the sub-array pairs, allowing for mismatched lengths
  return Array.from({ length: maxLength }, (_, index) => {
    const companionSubArray = companionMos2D[index] || []; // Default to empty array if missing
    const sapSubArray = sapMos2D[index] || []; // Default to empty array if missing

    return {
      delta: getJobsDelta(companionSubArray, sapSubArray),
    };
  });
}

type JobDeltaOptions = {
  skip?: boolean;
};

export const useJobDelta = (events: TEvent[] | undefined, options: JobDeltaOptions = {}) => {
  const { skip = false } = options;
  const [companionMos, setCompanionMos] = useState<string[][]>([]);
  const [sapMos, setSapMos] = useState<string[][]>([]);
  const asset = useSelectedAsset();

  const [fetchJobs, { loading: jobsLoading }] = useLazyQuery(GET_JOBS_BY_EVENT);
  const [fetchSapMos, { loading: sapLoading }] = useLazyQuery(GET_MO_NUMBERS_BY_REV_CODE);
  useEffect(() => {
    if (skip || !events || events.length === 0) {
      setCompanionMos([]);
      setSapMos([]);
      return;
    }

    const fetchCompanionJobs = async () => {
      const results = await Promise.all(
        events.map(async (event) => {
          if (!event.revisionCode) return [];

          try {
            const { data } = await fetchJobs({
              variables: { eventId: event.eventId },
            });

            const jobs = data?.jobsByEventId as TJob[];
            if (jobs) {
              const companionMos: string[] = jobs?.map((job) => job.moNumber) ?? [];
              return companionMos;
            }
          } catch (error) {
            console.error(`Failed to fetch Jobs for Event ${event.eventId}:`, error);
          }

          return [];
        })
      );

      setCompanionMos(results);
    };

    const fetchMosWithRevCode = async () => {
      const results = await Promise.all(
        events.map(async (event) => {
          if (!event.revisionCode) return [];

          try {
            const { data } = await fetchSapMos({
              variables: {
                region: asset?.regionName || '',
                site: asset?.assetName || '',
                revisionCode: event.revisionCode,
              },
            });

            const mos = data?.sapMoByRevCode as TSapMo[];
            if (mos) {
              const sapMos: string[] = mos?.map((mo) => mo.workOrder.replace(/^0+/, '')) ?? [];
              return sapMos;
            }
          } catch (error) {
            console.error(`Failed to fetch MOs for Rev Code ${event.revisionCode}:`, error);
          }

          return [];
        })
      );

      setSapMos(results);
    };
    fetchCompanionJobs();
    fetchMosWithRevCode();
  }, [events, skip, fetchJobs, fetchSapMos]);
  if (skip) {
    return { jobsDeltaWithEvent: events, loading: false };
  }
  const jobsDelta = getJobsDeltaForAll(companionMos, sapMos);
  const jobsDeltaWithEvent = jobsDelta.map((delta, index) => ({
    ...delta,
    ...events?.[index], // Add the eventId from the events array
  }));

  //const jobsDelta = getJobsDelta(companionMos,sapMos);
  return { jobsDeltaWithEvent, loading: jobsLoading };
};
